/**
 * Lời gọi API cho nhóm màn hình thiết bị. Không thành phần nào gọi fetch trực
 * tiếp; mọi lời gọi đi qua tệp này để có một chỗ duy nhất xử lý phong bì, token
 * và huỷ yêu cầu (AbortSignal) khi người dùng đổi bộ lọc liên tiếp.
 */
import { layToken, type PhongBiNoiBo } from './khach-api';

export interface TrangDuLieu<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export type MaTrangThaiThietBi = 'dang_phat' | 'cho_phat' | 'mat_ket_noi' | 'tam_dung' | 'chua_kich_hoat';

export interface DongThietBi {
  id: string;
  cumLoaId: string;
  tenThietBi: string | null;
  loaiThietBi: string;
  nguonId: string;
  tenXa: string | null;
  maThon: string | null;
  tenThon: string | null;
  maNhaCungCap: string;
  tenNhaCungCap: string;
  phuongThucKetNoi: 'truc_tiep' | 'gian_tiep';
  adapterId: string | null;
  trangThai: string;
  trangThaiHienThi: MaTrangThaiThietBi;
  amLuong: number | null;
  congSuat: number | null;
  nhietDo: number | null;
  noiDungPhat: string | null;
  phienBanUngDung: string | null;
  viDo: number | null;
  kinhDo: number | null;
  capNhatLuc: number | null;
  tuoiDuLieuGiay: number | null;
  nguonGoc: string | null;
  /** api (gián tiếp qua bộ tương thích) | mqtt | ws. */
  kenhKetNoi?: 'api' | 'mqtt' | 'ws';
  /** Gói trạng thái mới nhất gửi trung ương đã được xác nhận. */
  dongBoTrungUong?: boolean;
  /** TrangThaiKetNoi của DV008 mới nhất; null khi thiết bị không còn báo. */
  trangThaiKetNoi?: string | null;
  dungLuongSuDungKb?: number | null;
  /** Lịch phát mới nhất thiết bị đã xác nhận qua DV006 (thoiDiem: epoch giây). */
  lichPhatMoiNhat?: { lichPhatId: string; tenLichPhat: string | null; thoiDiem: number } | null;
}

export interface ChiTietThietBi extends DongThietBi {
  chiTiet: Record<string, unknown>;
}

export interface NutDiaBan {
  maDinhDanh: string;
  ten: string;
  cap: 'tinh' | 'xa' | 'thon';
  maCha: string | null;
  soThietBi: number;
}

export interface KetQuaHangLoat {
  tong: number;
  thanhCong: { cumLoaId: string; deviceId: string; moc: string }[];
  dangCho: { cumLoaId: string; deviceId: string; moc: string }[];
  loi: { cumLoaId: string; deviceId: string; maLoi: string; moTaLoi: string }[];
  soGianTiep: number;
}

/** Lỗi API đã dịch sang ngôn ngữ nghiệp vụ, mang theo mã lỗi để đối chiếu log. */
export class LoiApi extends Error {
  constructor(
    readonly ma: string,
    thongBao: string,
    readonly requestId?: string,
  ) {
    super(thongBao);
    this.name = 'LoiApi';
  }
}

function goc(): string {
  return (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';
}

async function goi<T>(
  duongDan: string,
  tuyChon: { phuongThuc?: string; than?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = layToken();
  const res = await fetch(`${goc()}${duongDan}`, {
    method: tuyChon.phuongThuc ?? 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(tuyChon.than !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: tuyChon.than !== undefined ? JSON.stringify(tuyChon.than) : undefined,
    signal: tuyChon.signal,
  });
  let pb: PhongBiNoiBo<T>;
  try {
    pb = (await res.json()) as PhongBiNoiBo<T>;
  } catch {
    throw new LoiApi('INTERNAL_ERROR', 'Máy chủ trả về dữ liệu không đọc được');
  }
  if (!pb.success) throw new LoiApi(pb.code, pb.message, pb.requestId);
  return pb.data as T;
}

function chuoiTruyVan(tham: Record<string, string | number | undefined | null>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(tham)) {
    if (v === undefined || v === null || v === '') continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export interface DongDaiTruyenThanh {
  nguonId: string;
  ten: string;
  ungDungCntt: boolean;
  tongThietBi: number;
  dangKetNoi: number;
  matKetNoi: number;
  tongBanTinDaPhat: number;
}

export interface DanhSachDaiTruyenThanh {
  tinh: DongDaiTruyenThanh & { soXaCntt: number; soXa: number };
  items: DongDaiTruyenThanh[];
  tinhLuc: number;
}

/** Số liệu một loại thiết bị trong một xã trên bản đồ số. */
export interface SoLieuLoaiBanDo {
  tong: number;
  ketNoi: number;
  matKetNoi: number;
  dangPhat: number;
  khongPhat: number;
}

/** Một điểm xã/phường trên bản đồ số (gomTheo=xa). */
export interface NhomXaBanDo {
  nguonId: string;
  ten: string;
  viDo: number;
  kinhDo: number;
  soThietBi: number;
  soMatKetNoi: number;
  loa: SoLieuLoaiBanDo;
  bang: SoLieuLoaiBanDo;
}

/**
 * Tải tệp Excel danh sách thiết bị theo bộ lọc (`GET /api/v1/devices/export`, bổ sung ngoài
 * OpenAPI) và cho trình duyệt lưu xuống. Trả số dòng đã xuất.
 */
async function xuatExcelThietBi(tham: Record<string, string | number | undefined>): Promise<number> {
  const token = layToken();
  const res = await fetch(`${goc()}/api/v1/devices/export${chuoiTruyVan(tham)}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const pb = (await res.json().catch(() => null)) as { code?: string; message?: string } | null;
    throw new LoiApi(pb?.code ?? 'INTERNAL_ERROR', pb?.message ?? `Máy chủ trả lỗi ${res.status}`);
  }
  const ten = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'thiet-bi.xlsx';
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ten;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return Number(res.headers.get('x-so-dong') ?? 0);
}

export const thietBiApi = {
  xuatExcel: xuatExcelThietBi,
  danhSach: (
    tham: Record<string, string | number | undefined | null>,
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<DongThietBi>> => goi(`/api/v1/devices${chuoiTruyVan(tham)}`, { signal }),

  chiTiet: (id: string, signal?: AbortSignal): Promise<ChiTietThietBi> =>
    goi(`/api/v1/devices/${id}`, { signal }),

  lichSuTrangThai: (
    id: string,
    tham: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ): Promise<{ mucDo: 'tho' | 'gio' | 'ngay'; diem: Record<string, unknown>[] }> =>
    goi(`/api/v1/devices/${id}/status-history${chuoiTruyVan(tham)}`, { signal }),

  nhatKy: (
    id: string,
    tham: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ): Promise<TrangDuLieu<Record<string, unknown>>> =>
    goi(`/api/v1/devices/${id}/logs${chuoiTruyVan(tham)}`, { signal }),

  baoTri: (
    id: string,
    signal?: AbortSignal,
  ): Promise<{ suKien: Record<string, unknown>[]; thieuBang: boolean }> =>
    goi(`/api/v1/devices/${id}/maintenance`, { signal }),

  banDo: (
    tham: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ): Promise<{
    diem: DongThietBi[];
    nhom: { viDo: number; kinhDo: number; soThietBi: number; soTrucTuyen: number; soMatKetNoi: number }[];
    /** Chỉ khi gọi với `gomTheo: 'xa'`: một điểm mỗi xã/phường (bản đồ số MH-01). */
    nhomXa?: NhomXaBanDo[];
    soKhongCoToaDo: number;
    gomNhom: boolean;
  }> => goi(`/api/v1/devices/map${chuoiTruyVan(tham)}`, { signal }),

  tao: (than: Record<string, unknown>): Promise<{ id: string }> =>
    goi('/api/v1/devices', { phuongThuc: 'POST', than }),

  sua: (id: string, than: Record<string, unknown>): Promise<{ id: string }> =>
    goi(`/api/v1/devices/${id}`, { phuongThuc: 'PUT', than }),

  xoa: (id: string): Promise<{ id: string }> => goi(`/api/v1/devices/${id}`, { phuongThuc: 'DELETE' }),

  guiLenh: (than: {
    maLenh: string;
    thamSo?: string;
    lyDo?: string;
    deviceIds: string[];
    /** "Bật loa": trả từng thiết bị về âm lượng trước khi tắt (bổ sung ngoài OpenAPI). */
    khoiPhucAmLuong?: boolean;
  }): Promise<KetQuaHangLoat> => goi('/api/v1/devices/commands', { phuongThuc: 'POST', than }),

  nhapKhau: (
    dong: Record<string, unknown>[],
    kiemThuTruoc: boolean,
  ): Promise<{
    tongDong: number;
    soHopLe: number;
    soCanhBao: number;
    soLoi: number;
    chiTiet: { dong: number; cumLoaId: string; ketQua: string; cot: string | null; lyDo: string | null }[];
    soDaNhap: number | null;
    cotHeThong: string[];
  }> => goi('/api/v1/devices/import', { phuongThuc: 'POST', than: { dong, kiemThuTruoc } }),

  tongQuan: (nguonId?: string, signal?: AbortSignal): Promise<Record<string, unknown>> =>
    goi(`/api/v1/dashboard/summary${chuoiTruyVan({ nguonId })}`, { signal }),

  /** Danh sách đài truyền thanh cấp xã (bổ sung ngoài OpenAPI). */
  danhSachDai: (
    signal?: AbortSignal,
    loaiThietBi?: 'cum_thu_phat_thanh' | 'bang_tin_dien_tu',
  ): Promise<DanhSachDaiTruyenThanh> =>
    goi(`/api/v1/statistics/stations${chuoiTruyVan({ loaiThietBi })}`, { signal }),

  cayDiaBan: (tham: { cap?: string; maCha?: string } = {}, signal?: AbortSignal): Promise<NutDiaBan[]> =>
    goi(`/api/v1/catalogs/administrative-units${chuoiTruyVan(tham)}`, { signal }),

  nhaCungCap: (signal?: AbortSignal): Promise<{ maNhaCungCap: string; ten: string }[]> =>
    goi('/api/v1/vendors', { signal }),

  boTuongThich: (
    signal?: AbortSignal,
  ): Promise<{ id: string; maNhaCungCap: string; tenNhaCungCap: string; camKetMs: number | null }[]> =>
    goi('/api/v1/vendor-adapters', { signal }),
};
